import React, { useCallback, useRef, useState } from 'react';
import { useTranslate } from '@translations/useTranslate';
import { CustomButton, CustomInputFeedback, FormContainer } from '@ui/components/atoms';
import ArrowIcon from '@assets/svg/arrow.svg';
import { IMappedVisitRequestResponse } from '@interfaces/VisitRequestLookups.interface';
import { Form, FormText } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { useVerifyOTP, useVisitRequestSendOTP } from '@services/VisitRequest';
import { AxiosError } from 'axios';
import VisitRequestOtpFormStyles from './VisitRequestOtpForm.module.scss';
import { ResendOtpMessageAndTimer } from './ResendOtpMessageAndTimer';

const RECAPTCHA_SITE_KEY = '6LdRwy4qAAAAAIN7oQMsZevTuDmW2m302maO2hwf';

export interface VisitRequestOtpFormProps {
  onSubmitOtp: (data: IMappedVisitRequestResponse) => void;
  requestId: string;
}
export const VisitRequestOtpForm: React.FC<VisitRequestOtpFormProps> = ({
  onSubmitOtp,
  requestId,
}: VisitRequestOtpFormProps) => {
  const { t } = useTranslate();
  const [canResend, setCanResend] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [showValidationForm, setShowValidationForm] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const { mutateAsync: mutateAsyncSendOTP, isLoading: isLoadingSendOTP } = useVisitRequestSendOTP();
  const { mutateAsync: mutateAsyncVerifyOtp, isLoading: isLoadingVerifyOTP } = useVerifyOTP();
  const [showResendButton, setShowResendButton] = useState(true);
  const otpFormScheme = Yup.object().shape({
    otp: Yup.string()
      .min(6, t('global.error.invalidInput'))
      .max(6, t('global.error.invalidInput'))
      .required(t('visit.request.error.otpIsRequired')),
  });

  const setErrorMessage = (error: AxiosError) => {
    const errorMessage =
      (error?.response?.data as { errorMessage?: string })?.errorMessage ?? t('global.error');
    setError(errorMessage);
  };

  const setEmptyError = () => {
    setError('');
  };

  const resetRecaptcha = () => {
    recaptchaRef.current?.reset();
    setRecaptchaToken('');
  };

  const verifyOTP = useCallback((otp: string) => {
    setEmptyError();
    mutateAsyncVerifyOtp(
      { otp: otp, requestId: requestId },
      {
        onError: (error) => {
          setErrorMessage(error);
          const errorCode = (error?.response?.data as { errorCode?: string })?.errorCode;
          if (errorCode === '408') {
            setShowResendButton(false);
          }
        },
      }
    )
      .then((data) => {
        onSubmitOtp(data);
      })
      .catch(() => {
        // Ignore Exception
      });
  }, []);

  const formik = useFormik({
    initialValues: {
      otp: '',
    },
    onSubmit: (values) => {
      verifyOTP(values.otp);
    },
    validationSchema: otpFormScheme,
    validateOnChange: false,
    validateOnBlur: true,
  });

  const onValidatePress = useCallback(() => {
    if (!recaptchaToken) {
      return;
    }
    setEmptyError();
    mutateAsyncSendOTP(
      { requestId, recaptchaToken },
      {
        onError: (error) => {
          setErrorMessage(error);
          const errorData = error?.response?.data as { errorCode?: string };
          const errorCode = errorData?.errorCode;
          if (errorCode && errorCode === '408') {
            setShowResendButton(false);
          }
        },
      }
    )
      .then(() => {
        setShowValidationForm(true);
        setShowResendButton(true);
      })
      .catch(() => {
        // Ignore Exception
      })
      .finally(() => {
        resetRecaptcha();
      });
  }, [mutateAsyncSendOTP, requestId, recaptchaToken]);

  const onResendCode = useCallback(
    (startTimer: () => void) => {
      if (!recaptchaToken) {
        return;
      }
      setEmptyError();
      mutateAsyncSendOTP(
        { requestId, recaptchaToken },
        {
          onError: (error) => {
            setErrorMessage(error);
            const errorData = error?.response?.data as { errorCode?: string };
            const errorCode = errorData?.errorCode;
            if (errorCode && errorCode === '408') {
              setShowResendButton(false);
            }
          },
        }
      )
        .then(() => {
          setShowResendButton(true);
          setTimeout(() => {
            startTimer();
          }, 1000);
        })
        .catch(() => {
          // Ignore Exception
        })
        .finally(() => {
          resetRecaptcha();
        });
    },
    [mutateAsyncSendOTP, requestId, recaptchaToken]
  );

  return (
    <FormContainer error={error}>
      {showValidationForm ? (
        <Form.Group className={VisitRequestOtpFormStyles.form_group}>
          <FormText className={VisitRequestOtpFormStyles.custom_subtitle}>
            {t('visit.request.otp.form.file.label')}
          </FormText>
          <Form.Control
            as="div"
            className={VisitRequestOtpFormStyles.custom_input_border}
            isInvalid={!!formik.errors.otp && formik.touched.otp}
          >
            <input
              {...formik.getFieldProps('otp')}
              className={VisitRequestOtpFormStyles.custom_input}
              maxLength={6}
              placeholder={' --- ---'}
            />
          </Form.Control>
          {formik.errors.otp && <CustomInputFeedback errorFeedback={formik.errors.otp} />}

          {showResendButton && (
            <>
              <ResendOtpMessageAndTimer
                resendCode={onResendCode}
                onTimerEnd={() => setCanResend(true)}
                onTimerStart={() => {
                  setCanResend(false);
                  setRecaptchaToken('');
                }}
              />
              {canResend && (
                <div className={VisitRequestOtpFormStyles.recaptcha_wrapper}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => setRecaptchaToken(token ?? '')}
                    onExpired={() => setRecaptchaToken('')}
                    onErrored={() => setRecaptchaToken('')}
                  />
                </div>
              )}
            </>
          )}

          <div className="d-flex justify-content-center align-items-center mt-5 mb-5">
            <CustomButton
              variant="primary"
              text={t('confirm')}
              disabled={isLoadingVerifyOTP}
              suffixIcon={ArrowIcon}
              onClick={formik.handleSubmit}
            />
          </div>
        </Form.Group>
      ) : (
        <div className={VisitRequestOtpFormStyles.validate_container}>
          <div className={VisitRequestOtpFormStyles.recaptcha_wrapper}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={(token) => setRecaptchaToken(token ?? '')}
              onExpired={() => setRecaptchaToken('')}
              onErrored={() => setRecaptchaToken('')}
            />
          </div>
          <CustomButton
            variant="primary"
            text={t('validate')}
            disabled={isLoadingSendOTP || !recaptchaToken}
            suffixIcon={ArrowIcon}
            onClick={onValidatePress}
          />
        </div>
      )}
    </FormContainer>
  );
};
